import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  TrendingUp, 
  Package, 
  Bell, 
  BarChart3, 
  Shield, 
  Zap,
  Sun,
  Moon,
  BookOpen,
  ArrowRight
} from 'lucide-react';

export function Landing() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const features = [
    {
      icon: TrendingUp,
      title: "Real-Time Profit Analysis",
      description: "Instant profit calculations with accurate UK FBA fees, prep costs, and shipping estimates."
    },
    {
      icon: Package,
      title: "Complete FBA Workflow",
      description: "Track acquisitions from sourcing to sale. Manage prep, shipments, and inventory in one place."
    },
    {
      icon: Bell,
      title: "Smart Alerts",
      description: "Get notified on price drops, profit opportunities, and rank improvements automatically."
    },
    {
      icon: BarChart3,
      title: "Market Intelligence",
      description: "Historical rank data, price trends, and seller competition analysis at your fingertips."
    },
    {
      icon: Shield,
      title: "Import Cost Tracking",
      description: "Automatic landed cost calculations for international sellers. Avoid margin erosion."
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Optimized for speed. Scan thousands of books and find profitable opportunities instantly."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-gold" />
            <span className="font-heading text-xl font-bold">Book Arbitrage Pro</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button 
              onClick={login}
              className="btn-gold"
              data-testid="login-button"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 via-transparent to-profit/5" />
        <div className="container mx-auto px-4 py-20 lg:py-32 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold border border-gold/20 mb-6">
              <Zap className="h-4 w-4" />
              <span className="text-sm font-medium">UK Amazon FBA Optimized</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
              Find Profitable Books.
              <br />
              <span className="text-gold">Automate Your FBA Business.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The intelligent book arbitrage platform that calculates real-time profits, 
              tracks your inventory workflow, and alerts you to the best opportunities.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                onClick={login}
                className="btn-gold text-lg px-8 h-12"
                data-testid="hero-cta"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 h-12"
              >
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y bg-muted/30">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="font-heading text-3xl lg:text-4xl font-black text-gold">100K+</div>
              <div className="text-sm text-muted-foreground mt-1">Books Tracked</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-3xl lg:text-4xl font-black text-profit">£50M+</div>
              <div className="text-sm text-muted-foreground mt-1">Profit Identified</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-3xl lg:text-4xl font-black text-foreground">5,000+</div>
              <div className="text-sm text-muted-foreground mt-1">Active Sellers</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-3xl lg:text-4xl font-black text-foreground">&lt;150ms</div>
              <div className="text-sm text-muted-foreground mt-1">Analysis Speed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
              Everything You Need to Scale
            </h2>
            <p className="text-muted-foreground">
              Purpose-built for UK Amazon FBA book sellers. Every feature designed to maximize your profits.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg hover:border-gold/20 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                    <feature.icon className="h-6 w-6 text-gold" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t bg-muted/30 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
            Ready to Find Your Next Profitable Book?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Join thousands of UK FBA sellers who trust Book Arbitrage Pro to power their business.
          </p>
          <Button 
            size="lg" 
            onClick={login}
            className="btn-gold text-lg px-8 h-12"
            data-testid="footer-cta"
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-gold" />
            <span className="font-heading font-bold">Book Arbitrage Pro</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Book Arbitrage Pro. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
